import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, RefreshCw, ShieldCheck, ShieldOff, ArrowUpRight } from "lucide-react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const kycStyle = {
  unverified: "text-white/45 bg-white/5",
  pending: "text-amber-400 bg-amber-400/10",
  verified: "text-emerald-400 bg-emerald-400/10",
  rejected: "text-rose-400 bg-rose-400/10",
};

const sourceStyle = {
  facebook: "text-[#1877f2] bg-[#1877f2]/10",
  instagram: "text-[#e1306c] bg-[#e1306c]/10",
  tiktok: "text-white bg-white/10",
  google: "text-[#4285f4] bg-[#4285f4]/10",
  affiliate: "text-amber-400 bg-amber-400/10",
};

const sourceLabel = {
  facebook: "Facebook",
  instagram: "Instagram",
  tiktok: "TikTok",
  google: "Google",
  affiliate: "Affiliate",
};

const AdminUsers = () => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { role: "user" };
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get("/admin/users", { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminLayout title="Users" subtitle="Customer accounts">
      <div className="bb-card rounded-2xl overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-white/5">
          <div className="text-[12px] text-white/55">{items.length} user{items.length === 1 ? "" : "s"}</div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 h-9 bg-white/[0.04] border border-white/8 rounded-lg">
              <Search size={14} className="text-white/40" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search email or name"
                className="bg-transparent outline-none text-[12px] text-white placeholder:text-white/35 w-56"
              />
            </div>
            <button onClick={load} className="w-9 h-9 rounded-lg border border-white/8 text-white/70 hover:text-white flex items-center justify-center">
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-[13px] min-w-[1080px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-white/40 font-semibold bg-white/[0.02]">
                <th className="text-left py-3 px-5">User</th>
                <th className="text-left py-3">Phone</th>
                <th className="text-left py-3">Source</th>
                <th className="text-right py-3">Deposited</th>
                <th className="text-right py-3">Balance</th>
                <th className="text-right py-3">Affiliate</th>
                <th className="text-left py-3">KYC</th>
                <th className="text-left py-3">Joined</th>
                <th className="text-left py-3">Status</th>
                <th className="text-right py-3 pr-5"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="py-3.5 px-5">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#3b59ff] to-[#1c3df5] flex items-center justify-center text-[11px] font-semibold">
                        {(u.name || "").slice(0, 1).toUpperCase()}
                      </div>
                      <div>
                        <div className="text-white font-medium">{u.name}</div>
                        <div className="text-white/45 text-[11px]">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="text-white/65 text-[12px] tabular-nums whitespace-nowrap pr-3" data-testid={`user-phone-${u.id}`}>
                    {u.phone || <span className="text-white/30">—</span>}
                  </td>
                  <td className="pr-3">
                    {u.referral_source ? (
                      <span
                        data-testid={`user-source-${u.id}`}
                        className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${sourceStyle[u.referral_source] || "text-white/55 bg-white/[0.05]"}`}
                      >
                        {sourceLabel[u.referral_source] || u.referral_source}
                      </span>
                    ) : (
                      <span className="text-white/30 text-[11px]">—</span>
                    )}
                  </td>
                  <td className="text-right text-emerald-400 font-medium tabular-nums" data-testid={`user-deposited-${u.id}`}>${fmt(u.total_deposited)}</td>
                  <td className="text-right text-white font-semibold tabular-nums" data-testid={`user-balance-${u.id}`}>${fmt(u.balance)}</td>
                  <td className="text-right text-violet-400 font-medium tabular-nums" data-testid={`user-affiliate-${u.id}`}>${fmt(u.affiliate_commission)}</td>
                  <td>
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${kycStyle[u.kyc_status] || kycStyle.unverified}`}>
                      {u.kyc_status}
                    </span>
                  </td>
                  <td className="text-white/65 text-[11px]">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td>
                    {u.suspended ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded text-rose-400 bg-rose-400/10">
                        <ShieldOff size={10} /> Suspended
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded text-emerald-400 bg-emerald-400/10">
                        <ShieldCheck size={10} /> Active
                      </span>
                    )}
                  </td>
                  <td className="text-right pr-5">
                    <Link to={`/admin/users/${u.id}`} className="inline-flex items-center gap-1 text-[12px] text-[#ffae6a] hover:text-white">
                      View <ArrowUpRight size={12} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && items.length === 0 && (
          <div className="py-16 text-center text-white/40 text-[13px]">No users found.</div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
